import { Navigate, useNavigate, useParams } from "react-router-dom"

import { Flex, Tabs } from "@/atoms"
import { NAMES_ROUTES, VALUES_PARAMS } from "@/const/routes.constants"
import { isUserAdmin } from "@/domain/user/user"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import { AuditOutlined } from "@/icons"
import { LinkButton } from "@/molecules/LinkButton"
import { Protected } from "@/molecules/Protected"
import { HeaderPage } from "@/organisms/HeaderPage"
import { RecurringReportsTab } from "@/pages/ReportsPage/components/organisms/RecurringReportsTab"
import { WarehouseReportTab } from "@/pages/ReportsPage/components/organisms/WarehouseReportsTab"
import { PageTemplate } from "@/templates/PageTemplate"
import { TabsTemplate } from "@/templates/TabsTemplate"
import { DEFAULT_ROUTE, REPORT_TAB_PARAM } from "./const"

import type { JobReport, ReportDefinition } from "@/domain/report"

export const ReportsPage = () => {
  const { ROOT, RECURRING, WAREHOUSE } = NAMES_ROUTES.REPORTS
  const formURL = `/${ROOT}/${RECURRING}`
  const createURL = `${formURL}/${VALUES_PARAMS.NEW_ENTITY}`
  const resource = "reports" as const
  const permissions = { resource, action: "write" } as const
  const navigate = useNavigate()
  const { user, hasPermission } = useUser()
  const isAdmin = isUserAdmin(user)
  const editDisabled = !hasPermission(permissions)
  const { logger, EVENTS } = useLogger()
  const { [REPORT_TAB_PARAM]: activeKey } = useParams<{
    [REPORT_TAB_PARAM]: string
  }>()
  const onEdit = (record: ReportDefinition | JobReport) => {
    if (editDisabled) return

    const needle = "definitionId" in record ? record.definitionId : record.id

    if (needle) {
      const { reportType } = record
      const meta = { needle, reportType }

      logger.info(EVENTS.ANALYTIC_EVENTS.REPORTS.EDIT_REPORT, meta)

      if (activeKey === WAREHOUSE) navigate(`${createURL}/${needle}`)
      else navigate(`${formURL}/${needle}`)
    }
  }

  const props = { permissions, isAdmin, editDisabled, createURL, onEdit }
  const { t } = useTranslation()

  if (activeKey !== RECURRING && activeKey !== WAREHOUSE)
    return <Navigate replace to={DEFAULT_ROUTE} />

  return (
    <PageTemplate
      full
      header={<HeaderPage titleTranslationKey="navigation.reports" />}
    >
      <Flex gap={16} vertical>
        <span />
        <TabsTemplate>
          <Tabs
            size="medium"
            activeKey={activeKey}
            items={[
              {
                key: WAREHOUSE,
                label: t(`navigation.reportsWarehouse`),
                children: <WarehouseReportTab {...props} />
              },
              {
                key: RECURRING,
                label: t(`navigation.reportsManager`),
                children: <RecurringReportsTab {...props} />
              }
            ]}
            tabBarExtraContent={
              <Protected shouldDisable permission={permissions}>
                <LinkButton
                  type="primary"
                  icon={<AuditOutlined />}
                  to={createURL}
                  onClick={() =>
                    logger.info(
                      EVENTS.ANALYTIC_EVENTS.REPORTS.CLICK_GENERATE_REPORT
                    )
                  }
                >
                  {t("reports.manage.generate")}
                </LinkButton>
              </Protected>
            }
            data-testid="tabs-reports"
            onChange={(key) => {
              navigate(`/${ROOT}/${key}`)
            }}
          />
        </TabsTemplate>
      </Flex>
    </PageTemplate>
  )
}
